import React, { useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { TemplateStudioModel } from 'common/contracts';
import { StoreBodyBlock } from 'constants/enums';
import { TemplateStudioBlock } from 'state/template-studio.state.service';
import { formatDateShort } from 'utils/formattingUtil';

import { cn } from 'components/lib/utils';
import { getOutdatedBlocks } from 'components/TemplatesStudio/utils';
import { Tooltip } from 'components/Tooltip/Tooltip';

import { mapBlockToTitle } from '../../FormPage/Sidebar/Main/Components/DesignBlockMenuItem';

import { TemplateActions } from './Actions';

const StatusBadge: React.FC<{
  text: string;
  bgColor: string;
  textColor: string;
}> = ({ text, bgColor, textColor }) => (
  <div
    className={cn(
      'flex items-center px-2 py-1 text-xs rounded-full cursor-default',
      bgColor
    )}
  >
    <span className={cn(textColor)}>{text}</span>
  </div>
);

const BlockBadge: React.FC<{ block: TemplateStudioBlock }> = ({ block }) => (
  <div className=" flex items-center gap-1 justify-center text-xs text-base-sidebarRing font-semibold">
    <FontAwesomeIcon icon={`fa-regular fa-circle-check text-xs`} />
    {mapBlockToTitle[block]}
  </div>
);

const CardTitle: React.FC<TemplateStudioModel> = ({
  templateName,
  updatedAt,
  isActive
}) => (
  <div className="template-info flex flex-col flex-1 gap-2">
    <div className="flex justify-start items-center gap-2">
      <div className={cn('text-base font-semibold')}>{templateName}</div>
      <StatusBadge
        text={isActive ? 'Active' : 'Draft'}
        bgColor={isActive ? 'bg-[#22C55E33]' : 'bg-[#40404033]'}
        textColor={cn(
          'font-light',
          isActive ? 'text-green-500' : 'text-neutral-700'
        )}
      />
    </div>
    <div className="flex flex-col cursor-default">
      {updatedAt && (
        <div className="text-sm text-base-mutedForeground">
          Last saved: {formatDateShort(updatedAt)}
        </div>
      )}
    </div>
  </div>
);

const UpdateReleasedBanner: React.FC = () => {
  return (
    <div className="flex gap-2 bg-green-50 h-[42px] rounded-t-lg p-3 text-sm items-center text-base-sidebarForeground cursor-default">
      <FontAwesomeIcon
        icon={`fa-thin fa-rocket`}
        className="text-sm font-thin"
      />
      <div className="font-semibold">New design available</div>
      <div className="w-[1px] h-4 bg-base-sidebarForeground" />
      <div>You can now apply it from the editor</div>
    </div>
  );
};

export const TemplateCard: React.FC<{ template: TemplateStudioModel }> = ({
  template
}) => {
  const { templateId, isActive, bundle, promotion, rollingOffers } = template;

  const [activeTemplateOutdatedBlocks, setActiveTemplateOutdatedBlocks] =
    useState<Set<TemplateStudioBlock>>(new Set());

  useEffect(() => {
    if (!isActive) return;

    const outdatedBlocks = getOutdatedBlocks(template);

    setActiveTemplateOutdatedBlocks(outdatedBlocks);
  }, [
    isActive,
    bundle?.selectedDesignId,
    promotion?.selectedDesignId,
    rollingOffers?.selectedDesignId
  ]);

  const isOutdated = useMemo(
    () =>
      isActive &&
      activeTemplateOutdatedBlocks &&
      activeTemplateOutdatedBlocks?.size > 0,
    [activeTemplateOutdatedBlocks, isActive]
  );

  const arrBodyBlocks: StoreBodyBlock[] = useMemo(() => {
    return [...template.bodyBlocksOrder, 'popup' as StoreBodyBlock];
  }, [template.bodyBlocksOrder]);

  return (
    <div
      className={cn(
        'template-card flex flex-col h-[140px] border border-base-input rounded-lg gap-6',
        isOutdated && 'h-[180px]'
      )}
      key={templateId}
      data-testid={`TemplatesStudio-TemplateCard-${templateId}`}
    >
      {isOutdated && <UpdateReleasedBanner />}
      <div
        className={cn(
          'template-card-content flex gap-6 px-6 align-start h-[90px] box-content',
          !isOutdated && 'py-6'
        )}
      >
        <div className="template-image w-[164px] self-stretch shrink-0 overflow-hidden rounded-lg border border-base-input bg-neutral-100">
          <img
            src={
              template.brandKit.backgroundImageDesktop ||
              'https://media.appcharge.com/defaults/template-studio/default_card_image.png'
            }
            alt="Template"
            className="h-full w-full min-h-full object-cover object-center"
          />
        </div>
        <div className="template-info-wrapper flex flex-col flex-1 gap-2 h-full">
          <CardTitle {...template} />
          <div className="active-blocks flex items-center gap-2 cursor-default">
            {arrBodyBlocks.slice(0, 3).map((block, index) => (
              <React.Fragment key={block}>
                <BlockBadge block={block} />
                {index < Math.min(3, arrBodyBlocks.length) - 1 && (
                  <div className="w-px h-4 bg-base-sidebarBorder" />
                )}
              </React.Fragment>
            ))}
            {arrBodyBlocks.length > 3 && (
              <>
                <div className="w-px h-4 bg-base-sidebarBorder" />
                <Tooltip
                  content={
                    <div className="flex flex-col gap-2">
                      {arrBodyBlocks.slice(3).map((block) => (
                        <div key={block}>{mapBlockToTitle[block]}</div>
                      ))}
                    </div>
                  }
                >
                  <div className="text-xs text-base-sidebarRing font-semibold">
                    And +{arrBodyBlocks.length - 3} more
                  </div>
                </Tooltip>
              </>
            )}
          </div>
        </div>
        <TemplateActions template={template} />
      </div>
    </div>
  );
};
